import axios from 'axios';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { API_BASE_URL } from '@/config';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import loader from '@/assets/img/loader.svg';
import {
  CompareArrows,
  DeleteForever,
  Edit,
  Settings,
} from '@mui/icons-material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TablePaginationActions from '@/views/Managefile/TablePaginationActions/TablePaginationActions';
import { useGetFacilities } from '@/api/hooks/facilities/useGetFacilities';

export default function Facilities({ setErrorMessage, organizationId }) {
  const { t } = useTranslation();
  const [search_keyword, setSearchKeyword] = useState('');
  const [user, setUser] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const {
    facilities,
    loading: loadingFacilities,
    error,
    refetch,
  } = useGetFacilities({
    organizationId,
    search: search_keyword,
    allColumns: true,
  });

  const handleKeyUp = (event) => {
    setSearchKeyword(event.target.value);
  };

  const changestatus = (id) => {
    setLoading(true);
    let token = user && user.accessToken;
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(API_BASE_URL + '/updatestatus/' + id, {
        headers: headers,
      })
      .then((response) => {
        return response;
      })
      .then((facilitiestatus) => {
        if (
          facilitiestatus &&
          facilitiestatus.data &&
          facilitiestatus.data.success
        ) {
          refetch();
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const deleteFacility = (id) => {
    if (window.confirm(t('facilities.confirmDelete'))) {
      setLoading(true);
      let token = user && user.accessToken;
      const headers = { Authorization: `Bearer ${token}` };
      axios
        .delete(API_BASE_URL + '/delete_facility/' + id, {
          headers: headers,
        })
        .then(() => {
          refetch();
          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPage = (event) => {
    let rows = parseInt(event.target.value);
    setRowsPerPage(rows);
    setPage(0);
  };

  useEffect(() => {
    let state = localStorage['appState'];
    let AppState;
    if (state) {
      AppState = JSON.parse(state);
      setUser(AppState.user);
    }
    if (AppState && AppState.user && AppState.user.role != 'admin') {
      window.location.href = '/client/error';
    }
  }, []);

  // Handle error messages from the hook
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error, setErrorMessage]);

  const pullFilesFromSftp = async (id) => {
    try {
      const isConfirmed = window.confirm(t('facilities.confirmPullFiles'));
      if (!isConfirmed) {
        return;
      }
      setLoading(true);
      await axios.get(API_BASE_URL + '/getFilesFromSftp/' + id);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  };
  const createFacilityLink = organizationId
    ? `/admin/createFacility?organizationId=${organizationId}`
    : '/admin/createFacility';

  const getUpdateFacilityLink = (id) => {
    return organizationId
      ? `/admin/updateFacility/${id}?organizationId=${organizationId}`
      : `/admin/updateFacility/${id}`;
  };

  return (
    <div>
      {(loadingFacilities || loading) && (
        <div className='loaders'>
          <img src={loader} />
        </div>
      )}
      <div className='t-header'>
        <div className='t-header-left'>
          <Link to={createFacilityLink} className='link'>
            <span className='add-icon'>
              <AddIcon color='primary' />
            </span>
            {t('facilities.createButton')}
          </Link>
        </div>
        <div className='t-header-right'>
          <div className='custom-search-box'>
            <input
              type='search'
              className='seacrh-field'
              value={search_keyword}
              onChange={handleKeyUp}
              placeholder={t('facilities.searchPlaceholder')}
            />
            <span className='search-icon'>
              <SearchIcon color='primary' />
            </span>
          </div>
        </div>
      </div>

      <Table width='100%' id='template'>
        <TableHead>
          <TableRow>
            <TableCell align='left'>
              {t('facilities.table.facilityName')}
            </TableCell>
            <TableCell align='left'>{t('facilities.table.email')}</TableCell>
            <TableCell align='left'>{t('facilities.table.phone')}</TableCell>
            <TableCell align='left'>
              {t('facilities.table.bankAccount')}
            </TableCell>
            <TableCell align='left'>
              {t('facilities.table.routingNumber')}
            </TableCell>
            <TableCell align='left'>{t('facilities.table.status')}</TableCell>
            <TableCell align='left'>{t('facilities.table.actions')}</TableCell>
          </TableRow>
        </TableHead>

        {facilities && facilities.length > 0 ? (
          <TableBody>
            {(rowsPerPage > 0
              ? facilities.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage,
                )
              : facilities
            ).map((obj, index) => (
              <TableRow className='txtalign' key={index}>
                <TableCell width='20%'>
                  {obj.name} <br />
                  {obj.id && (
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: '#555',
                      }}>
                      ({obj.id})
                    </span>
                  )}
                </TableCell>
                <TableCell width='20%'>{obj.email}</TableCell>
                <TableCell width='20%'>{obj.phone}</TableCell>
                <TableCell width='20%'>{obj.bank_acc}</TableCell>
                <TableCell width='20%'>{obj.routing_no}</TableCell>
                <TableCell width='20%'>
                  {obj.status && (
                    <a
                      style={{
                        cursor: 'pointer',
                        color: obj.status == 1 ? '' : '#ff0000',
                      }}
                      onClick={() => changestatus(obj.id)}>
                      {obj.status == 1
                        ? t('facilities.status.active')
                        : t('facilities.status.inactive')}
                    </a>
                  )}
                </TableCell>
                <TableCell width='20%'>
                  <Link
                    to={getUpdateFacilityLink(obj.id)}
                    title={t('facilities.updateTitle')}>
                    <Edit />{' '}
                  </Link>

                  <a
                    style={{ cursor: 'pointer' }}
                    onClick={() => deleteFacility(obj.id)}
                    title={t('facilities.deleteTitle')}>
                    {' '}
                    <DeleteForever />
                  </a>

                  <a
                    style={{ cursor: 'pointer' }}
                    onClick={() => pullFilesFromSftp(obj.id)}
                    title={t('facilities.pullFilesTitle')}>
                    {' '}
                    <CompareArrows />
                  </a>

                  <Link
                    to={`/admin/facility-settings/${obj.id}`}
                    title={t('facilities.settingsTitle')}>
                    <Settings />
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        ) : (
          <TableBody>
            <TableRow>
              <TableCell>{t('facilities.noData')}</TableCell>
            </TableRow>
          </TableBody>
        )}
        <TableFooter>
          <TableRow align='center'>
            <TablePagination
              rowsPerPageOptions={[
                5,
                10,
                25,
                { label: t('common.labels.all'), value: -1 },
              ]}
              colSpan={3}
              count={facilities.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { roboto: 'rows per page' },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}
